/**
 * Idle timeout (11 §2.1: 6 h by default, configurable 15–360 minutes per workspace, #163 a). The app refreshes
 * tokens on its own, which would keep Keycloak's SSO session alive forever while a tab is open, so inactivity is
 * measured here:
 * a warning `warnBeforeMs` before the end, then a real sign-out (the Keycloak session ends too).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, DialogContent, DialogRoot } from "@brevalt/ui";
import { useErrorToast } from "../errors/toasts";
import { useAuth } from "./provider";

// Wheel and keys cover scrolling (a scroll event does not bubble and fires for programmatic scrolls too).
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart"] as const;
/** Activity of every tab of the app (timestamps only, nothing stored): one tab in use keeps all signed in. */
export const ACTIVITY_CHANNEL = "brevalt.activity";
/** At most one broadcast per this many ms (activity events are frequent). */
const BROADCAST_EVERY_MS = 5_000;

export interface IdleState {
  warning: boolean;
  remainingMs: number;
  /** "Stay signed in": counts as activity, in every tab. */
  stay: () => void;
}

type ActivityChannel = Pick<BroadcastChannel, "postMessage" | "addEventListener" | "removeEventListener" | "close">;

export function useIdleTimer({
  timeoutMs,
  warnBeforeMs,
  onTimeout,
  now = Date.now,
  tickMs = 1000,
  channel: channelProp,
}: {
  timeoutMs: number;
  warnBeforeMs: number;
  /** May fail (e.g. the sign-out); the timer then tries again on the next tick. */
  onTimeout: () => void | Promise<void>;
  now?: () => number;
  tickMs?: number;
  /** Test seam; default `new BroadcastChannel(ACTIVITY_CHANNEL)`. */
  channel?: ActivityChannel | null;
}): IdleState {
  const last = useRef(now());
  const lastBroadcast = useRef(0);
  const [remainingMs, setRemaining] = useState(timeoutMs);
  const timedOut = useRef(false);
  const onTimeoutRef = useRef(onTimeout);
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  });
  const channelRef = useRef<ActivityChannel | null>(null);

  const trailing = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Throttled, with a trailing message: the latest activity always reaches the other tabs within the window.
  const broadcast = useCallback((at: number, force = false) => {
    const wait = BROADCAST_EVERY_MS - (at - lastBroadcast.current);
    if (!force && wait > 0) {
      trailing.current ??= setTimeout(() => {
        trailing.current = null;
        lastBroadcast.current = last.current;
        channelRef.current?.postMessage({ at: last.current });
      }, wait);
      return;
    }
    lastBroadcast.current = at;
    channelRef.current?.postMessage({ at });
  }, []);

  const stay = useCallback(() => {
    const at = now();
    last.current = at;
    setRemaining(timeoutMs);
    broadcast(at, true);
  }, [now, timeoutMs, broadcast]);

  useEffect(() => {
    const channel =
      channelProp !== undefined
        ? channelProp
        : typeof BroadcastChannel === "undefined"
          ? null
          : new BroadcastChannel(ACTIVITY_CHANNEL);
    channelRef.current = channel;
    // Activity in this tab only counts before its warning shows (a stray mouse move must not hide a security
    // prompt); activity reported by another tab always counts (someone is using the app).
    const mark = () => {
      const at = now();
      if (at - last.current < timeoutMs - warnBeforeMs) {
        last.current = at;
        broadcast(at);
      }
    };
    const remote = (e: Event) => {
      const data = (e as MessageEvent<{ at?: unknown; query?: unknown } | null>).data;
      // Another tab's warning started: tell it when this tab was last used.
      if (data?.query === true) {
        channelRef.current?.postMessage({ at: last.current });
        return;
      }
      const at = data?.at;
      if (typeof at === "number" && Number.isFinite(at) && at <= now() + 1000 && at > last.current) {
        last.current = at;
        timedOut.current = false;
        setRemaining(timeoutMs - (now() - at));
      }
    };
    channel?.addEventListener("message", remote);
    for (const e of ACTIVITY_EVENTS) window.addEventListener(e, mark, { passive: true });
    let warned = false;
    const id = setInterval(() => {
      const left = timeoutMs - (now() - last.current);
      setRemaining(left);
      if (left <= warnBeforeMs && left > 0 && !warned) {
        // The warning starts: share this tab's last activity and ask the others for theirs.
        warned = true;
        broadcast(last.current, true);
        channel?.postMessage({ query: true });
      } else if (left > warnBeforeMs) {
        warned = false;
      }
      if (left <= 0 && !timedOut.current) {
        timedOut.current = true;
        Promise.resolve()
          .then(() => onTimeoutRef.current())
          .catch(() => {
            // Try again on the next tick.
            timedOut.current = false;
          });
      }
    }, tickMs);
    return () => {
      for (const e of ACTIVITY_EVENTS) window.removeEventListener(e, mark);
      channel?.removeEventListener("message", remote);
      if (channelProp === undefined) channel?.close();
      clearInterval(id);
      if (trailing.current !== null) clearTimeout(trailing.current);
      trailing.current = null;
    };
  }, [now, timeoutMs, warnBeforeMs, tickMs, channelProp, broadcast]);

  return { warning: remainingMs <= warnBeforeMs && remainingMs > 0, remainingMs: Math.max(0, remainingMs), stay };
}

const mmss = (ms: number) => {
  const s = Math.ceil(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/** The warning dialog and the sign-out at the end. */
export function IdleTimeout({ minutes = 30, warnMinutes = 5 }: { minutes?: number; warnMinutes?: number }) {
  const { t } = useTranslation();
  const session = useAuth();
  const toastError = useErrorToast();
  const idle = useIdleTimer({
    timeoutMs: minutes * 60_000,
    warnBeforeMs: warnMinutes * 60_000,
    // A failed sign-out is retried by the timer.
    onTimeout: () => session.signOut("idle"),
  });
  return (
    <DialogRoot open={idle.warning}>
      <DialogContent
        hideClose
        title={t("shell.you_ll_be_signed_out_soon")}
        description={t("shell.you_ve_been_inactive", { time: mmss(idle.remainingMs) })}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                session.signOut("user").catch(toastError);
              }}
            >
              {t("shell.sign_out")}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                idle.stay();
                session.refresh().then((ok) => {
                  if (!ok) toastError(new Error("refresh failed"));
                }, toastError);
              }}
            >
              {t("shell.stay_signed_in")}
            </Button>
          </>
        }
      />
    </DialogRoot>
  );
}
